import { useMemo } from "react";
import { encode } from "uqr";

/**
 * A QR code drawn as inline SVG from `uqr`'s module matrix (issue #48): made
 * in the browser, no service asked, nothing to allow in the CSP.
 *
 * Always black on white, in dark mode too. That is the one place a colour is
 * written directly instead of a token: phone cameras read dark modules on a
 * light ground reliably, and an inverted code fails on many of them.
 */
export function QrCode({ text, label, className = "" }: { text: string; label: string; className?: string }) {
  const { size, path } = useMemo(() => {
    // Medium error correction survives a projector's glare or a crumpled print.
    const qr = encode(text, { ecc: "M", border: 4 });
    let d = "";
    qr.data.forEach((row, y) => {
      row.forEach((dark, x) => {
        if (dark) d += `M${x} ${y}h1v1h-1z`;
      });
    });
    return { size: qr.size, path: d };
  }, [text]);

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={label}
      shapeRendering="crispEdges"
      className={className}
    >
      <rect width={size} height={size} fill="#fff" />
      <path d={path} fill="#000" />
    </svg>
  );
}
